import React from 'react';
import { cn } from '../../../lib/utils';
import TailwindInput from '../../Tailwind/TailwindInput';
import TailwindButton from '../../Tailwind/TailwindButton';
import { SalesforceFormData, SalesforceConnectionDetails } from '../types';

interface SalesforceFormProps {
    form: SalesforceFormData;
    setForm: React.Dispatch<React.SetStateAction<SalesforceFormData>>;
    isEditing: boolean;
    connection?: SalesforceConnectionDetails | null;
    privateKeyInputMode: 'text' | 'file';
    setPrivateKeyInputMode: (mode: 'text' | 'file') => void;
    isCreateMode?: boolean;
}

export default function SalesforceForm({
    form,
    setForm,
    isEditing,
    connection,
    privateKeyInputMode,
    setPrivateKeyInputMode,
    isCreateMode = false,
}: SalesforceFormProps) {
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const content = event.target?.result as string;
                setForm(prev => ({ ...prev, sf_private_key: content }));
            };
            reader.readAsText(file);
        }
    };

    const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
        <div>
            <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
            <p className="text-sm text-slate-900 break-all">{value || '—'}</p>
        </div>
    );

    // Read-only view
    if (!isEditing && !isCreateMode && connection) {
        return (
            <div className="space-y-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Connection Settings
                </p>

                <ReadOnlyField label="Connection Name" value={connection.name} />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <ReadOnlyField label="Instance URL" value={connection.sf_instance_url} />
                    <ReadOnlyField label="Domain" value={connection.sf_domain} />
                </div>

                <div className="border-t border-slate-100" />

                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Authentication
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <ReadOnlyField label="Username" value={connection.sf_username} />
                    <ReadOnlyField label="Client ID (Consumer Key)" value={connection.sf_client_id} />
                </div>

                <div>
                    <p className="text-xs font-medium text-slate-500 mb-1">Private Key</p>
                    <p className="text-sm text-slate-400 italic">Hidden for security</p>
                </div>
            </div>
        );
    }

    // Edit/Create form
    return (
        <div className="space-y-5">
            <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Connection Settings
                </p>
                <p className="text-xs text-slate-500 mt-1">
                    Configure your Salesforce connection. You can find these details in your Salesforce Connected App settings.
                </p>
            </div>

            <TailwindInput
                label="Connection Name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                required
                helperText="A friendly name to identify this connection"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TailwindInput
                    label="Instance URL"
                    value={form.sf_instance_url}
                    onChange={(e) => setForm(prev => ({ ...prev, sf_instance_url: e.target.value }))}
                    fullWidth
                    required
                    placeholder="https://your-instance.salesforce.com"
                    helperText="Your Salesforce instance URL"
                />
                <TailwindInput
                    label="Domain"
                    value={form.sf_domain}
                    onChange={(e) => setForm(prev => ({ ...prev, sf_domain: e.target.value }))}
                    fullWidth
                    placeholder="login.salesforce.com"
                    helperText="Use 'login.salesforce.com' for production"
                />
            </div>

            <div className="border-t border-slate-100" />

            <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Authentication
                </p>
                <p className="text-xs text-slate-500 mt-1">
                    JWT Bearer Flow credentials from your Salesforce Connected App.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TailwindInput
                    label="Username"
                    value={form.sf_username}
                    onChange={(e) => setForm(prev => ({ ...prev, sf_username: e.target.value }))}
                    fullWidth
                    required
                    helperText="Salesforce username (email format)"
                />
                <TailwindInput
                    label="Client ID (Consumer Key)"
                    value={form.sf_client_id}
                    onChange={(e) => setForm(prev => ({ ...prev, sf_client_id: e.target.value }))}
                    fullWidth
                    required
                    helperText="Found in your Connected App settings"
                />
            </div>

            {/* Private Key Section */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700 px-1">Private Key</label>
                    <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setPrivateKeyInputMode('file')}
                            className={cn(
                                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                                privateKeyInputMode === 'file'
                                    ? 'bg-primary text-white'
                                    : 'bg-white text-slate-600 hover:bg-slate-50'
                            )}
                        >
                            <span className="material-symbols-outlined text-[14px]">upload_file</span>
                            File
                        </button>
                        <button
                            type="button"
                            onClick={() => setPrivateKeyInputMode('text')}
                            className={cn(
                                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-200',
                                privateKeyInputMode === 'text'
                                    ? 'bg-primary text-white'
                                    : 'bg-white text-slate-600 hover:bg-slate-50'
                            )}
                        >
                            <span className="material-symbols-outlined text-[14px]">text_fields</span>
                            Text
                        </button>
                    </div>
                </div>

                {privateKeyInputMode === 'file' ? (
                    <div>
                        <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg cursor-pointer transition-colors">
                            <span className="material-symbols-outlined text-[18px]">upload_file</span>
                            Upload .pem file
                            <input
                                type="file"
                                hidden
                                accept=".pem,.key"
                                onChange={handleFileUpload}
                            />
                        </label>
                        {form.sf_private_key && (
                            <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                Private key loaded ({form.sf_private_key.length} characters)
                            </div>
                        )}
                        {!isCreateMode && (
                            <p className="text-xs text-slate-400 mt-1.5">
                                Only upload if you want to update the private key
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col gap-1.5 w-full">
                        <textarea
                            value={form.sf_private_key}
                            onChange={(e) => setForm(prev => ({ ...prev, sf_private_key: e.target.value }))}
                            rows={4}
                            placeholder="Paste your private key here..."
                            className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm rounded-lg px-4 py-2.5 transition-colors focus:ring-primary focus:border-primary focus:outline-none placeholder:text-slate-400 resize-none"
                        />
                        {!isCreateMode && (
                            <p className="text-xs text-slate-400">
                                Only fill this if you want to update the private key
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
